import { useState, useMemo } from 'react';
import { Row, Col, Card, Button, Table, Form, Tabs, Tab, Badge, Alert } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { FaSort, FaSortUp, FaSortDown, FaBookOpen, FaCoins, FaCheckCircle, FaQuestion, FaMoneyBillWave, FaCheck, FaTrash, FaUndo } from 'react-icons/fa';
import Message from '../components/Message';
import Loader from '../components/Loader';
import {
    useDeliverOrderMutation,
    useGetOrderDetailsQuery,
    useMarkOrderPaidMutation,
    useToggleAcceptItemMutation,
    useDeleteOrderItemMutation,
    useUndoRemoveItemMutation
} from '../slices/ordersApiSlice';
import { BASE_URL } from '../constants';

const OrderScreen = () => {
    const { id: orderId } = useParams();
    const navigate = useNavigate();

    const { data: order, refetch, isLoading, error } = useGetOrderDetailsQuery(orderId);
    const [deliverOrder, { isLoading: loadingDeliver }] = useDeliverOrderMutation();
    const [markOrderPaid, { isLoading: loadingMarkPaid }] = useMarkOrderPaidMutation();
    const [toggleAccept] = useToggleAcceptItemMutation();
    const [deleteItem] = useDeleteOrderItemMutation();
    const [undoItem] = useUndoRemoveItemMutation();
    const { userInfo } = useSelector((state) => state.auth);

    // UI states for items table
    const [search, setSearch] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'isbn', direction: 'asc' });
    const [page, setPage] = useState(1);
    // const pageSize = 100;
    const [pageSize, setPageSize] = useState(25);
    const [downloadingLabel, setDownloadingLabel] = useState(false);

    // Safety: when order is not loaded yet, guard below by returning early (Loader)
    const deliverHandler = async () => {
        try {
        await deliverOrder(orderId).unwrap();
        await refetch();
        toast.success('Order gemarkeerd als ontvangen');
        } catch (err) {
        toast.error(err?.data?.message || err?.message || 'Fout bij markeren als ontvangen');
        }
    };

    const markPaidHandler = async () => {
        try {
        await markOrderPaid(order._id).unwrap();
        await refetch();
        toast.success('Order gemarkeerd als betaald');
        } catch (err) {
        toast.error(err?.data?.message || err?.message || 'Fout bij markeren als betaald');
        }
    };

    const downloadShippingLabel = async () => {
        try {
            setDownloadingLabel(true);
            const response = await fetch(`${BASE_URL}/api/orders/${orderId}/label/download`, {
                credentials: 'include',
            });
            if (!response.ok) {
                const responseData = await response.json().catch(() => ({}));
                throw new Error(responseData.message || 'Het verzendlabel kon niet worden gedownload.');
            }

            const blob = await response.blob();
            const objectUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = objectUrl;
            link.download = `DHL-verzendlabel-${orderId}.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(objectUrl);
        } catch (downloadError) {
            toast.error(downloadError.message || 'Het verzendlabel kon niet worden gedownload.');
        } finally {
            setDownloadingLabel(false);
        }
    };

    // Accep order actions
    const handleToggleAccept = async (itemId) => {
        if (!orderId) {
            toast.error("Order ID ontbreekt");
            return;
        }

        try {
            await toggleAccept({ orderId, itemId }).unwrap();
            refetch();
        } catch (err) {
            toast.error("Het item kon niet worden bijgewerkt.");
        }
    };

    // Delete item
    const handleDeleteItem = async (itemId) => {
        if (!orderId) {
            toast.error("Order ID ontbreekt");
            return;
        }

        try {
            await deleteItem({ orderId, itemId }).unwrap();
            refetch();
        } catch (err) {
            toast.error("Het item kon niet worden verwijderd.");
        }
    };

    // Undo removed item
    const handleUndoItem = async (removedItemId) => {
        if (!orderId) {
            toast.error("Order ID ontbreekt");
            return;
        }

        try {
            await undoItem({ orderId, removedItemId }).unwrap();
            await refetch();
            toast.success('Item teruggezet');
        } catch (err) {
            toast.error(err?.data?.message || err?.message || 'Het item kon niet worden teruggezet.');
        }
    };
    
    // Items list (safe)
    const searchableItems = useMemo(() => {
        if (!order) return [];

        const active = (order.orderItems || []).map((it) => ({
            ...it,
            _status: 'active',
        }));

        const removed = (order.removedItems || []).map((it) => ({
            ...it,
            _status: 'deleted',
        }));

        return [...active, ...removed];
    }, [order]);

    // Filter (search by isbn, title, author)
    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return searchableItems;

        return searchableItems.filter((it) => {
            const isbn = String(it.isbn || '').toLowerCase();
            const title = String(it.title || '').toLowerCase();
            const author = String(it.author || '').toLowerCase();

            return (
            isbn.includes(q) ||
            title.includes(q) ||
            author.includes(q)
            );
        });
    }, [searchableItems, search]);

    // Sorting for items
    const sorted = useMemo(() => {
    const list = [...filtered];

    list.sort((a, b) => {
        // 0) Active first, deleted last
        if (a._status !== b._status) {
            return a._status === 'deleted' ? 1 : -1;
        }

        // 1) Non-accepted first
        if (a.isAccepted !== b.isAccepted) {
        return a.isAccepted ? 1 : -1;
        }

        // 2) Secondary sort by chosen column
        const { key, direction } = sortConfig;
        let valA, valB;

        switch (key) {
        case 'isbn':
            valA = String(a.isbn || '').toLowerCase();
            valB = String(b.isbn || '').toLowerCase();
            break;
        case 'title':
            valA = String(a.title || '').toLowerCase();
            valB = String(b.title || '').toLowerCase();
            break;
        case 'author':
            valA = String(a.author || '').toLowerCase();
            valB = String(b.author || '').toLowerCase();
            break;
        case 'price':
            valA = Number(a.accepted_price ?? a.offer_price ?? a.price ?? 0);
            valB = Number(b.accepted_price ?? b.offer_price ?? b.price ?? 0);
            break;
        default:
            return 0;
        }

        if (valA < valB) return direction === 'asc' ? -1 : 1;
        if (valA > valB) return direction === 'asc' ? 1 : -1;
        return 0;
    });

    return list;
    }, [filtered, sortConfig]);

    // Pagination
    const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));

    const paginated = sorted.slice(
    (page - 1) * pageSize,
    page * pageSize
    );

    const handleSort = (key) => {
        setPage(1);
        setSortConfig((prev) => {
        if (prev.key === key) {
            return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
        }
        return { key, direction: 'asc' };
        });
    };

    const renderSortIcon = (key) => {
        if (sortConfig.key !== key) return <FaSort />;
        return sortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />;
    };

    // Early loading / error handling
    if (isLoading) return <Loader />;
    if (error) return <Message variant="danger">{error?.data?.message || error?.error || 'Fout'}</Message>;
    if (!order) return <Message variant="info">Bestelling niet gevonden</Message>;

    // Top info row: user name, order date, order id
    const userName = order.user?.name || 'Gebruiker';
    const orderDate = order.createdAt ? order.createdAt.substring(0, 10) : '—';

    const totalItems = order?.orderItems.length;
    const totalRemoved = order?.removedItems.length;
    const shippingLabel = order.shippingLabel;
    const hasAvailableShippingLabel = shippingLabel?.status === 'success'
        || (Boolean(shippingLabel?.labelId) && shippingLabel?.status !== 'error');
    const removedItemsPrice = order?.removedItems?.reduce(
        (acc, item) => acc + Number(item.accepted_price ?? item.offer_price ?? item.price ?? 0),
        0
    );

    return (
        <>
            {/* Top info row */}
            <Row className="align-items-center mb-3">
                <h1 className="display-6 display-md-5 text-center">{userName}</h1>
                <Col md={6}>
                <div className="text-muted">Order datum: {orderDate}</div>
                </Col>
                <Col md={6}>
                <div className="text-muted text-md-end">Order ID: {order._id}</div>
                </Col>
            </Row>
            <Button
                variant="outline-primary"
                className="mb-3"
                style={{ borderRadius: "10px", padding: "6px 16px" }}
                onClick={() => navigate(-1)}
                >
                ← Terug
            </Button>
            
            {/* SummaryHeader (Option A: order-level) */}
            <Row className="mb-4">
                <Col md={12}>
                    <Card className="shadow-sm p-4 bg-white rounded-3 order-summary-card">
                        <Row className="align-items-center justify-content-center text-center g-4">
                            {/* TOTAL BOOKS */}
                            <Col md={2} xs={6} className="summary-block">
                            <FaBookOpen className="text-primary fs-3 mb-2" />
                            <div className="fw-semibold">Totaal Boeken</div>
                            <div className="fs-5">{order.orderItems.length}</div>
                            </Col>

                            {/* Separator */}
                            <Col md={1} className="d-none d-md-block">
                            <div className="separator"></div>
                            </Col>

                            {/* TOTAL PRICE */}
                            <Col md={2} xs={6} className="summary-block">
                            <FaCoins className="text-primary fs-3 mb-2" />
                            <div className="fw-semibold">Totaal te Betalen</div>
                            <div className="fs-5">€ {Number(order.itemsPrice).toFixed(2)}</div>
                            </Col>

                            {/* Separator */}
                            <Col md={1} className="d-none d-md-block">
                            <div className="separator"></div>
                            </Col>

                            <Col md={2} xs={6} className="summary-block">
                            <FaTrash className="text-primary fs-3 mb-2" />
                            <div className="fw-semibold">Totaal Verwijderd ({order.removedItems.length})</div>
                            <div className="fw-semibold">€ {removedItemsPrice.toFixed(2)}</div>
                            </Col>

                            {/* Separator */}
                            <Col md={1} className="d-none d-md-block">
                            <div className="separator"></div>
                            </Col>

                            <Col md={2} xs={6} className="summary-block">
                                {/* DELIVER BUTTON */}
                                {loadingDeliver && <Loader />}
                                {userInfo?.isAdmin && !order.isDelivered ? (
                                    <Button
                                    className="w-100 py-2 action-button deliver-btn"
                                    onClick={deliverHandler}
                                    >
                                    <FaCheckCircle className="me-2" /> Mark as Geleverd
                                    </Button>
                                ) : ( 
                                <Col>
                                    <div className="fw-semibold">
                                        { !order.isDelivered ? <FaQuestion className="text-primary fs-6 mb-2" /> : <FaCheck className="text-primary fs-6 mb-2" /> }
                                        {' '} Geleverd 
                                    </div>
                                    {/* <div className="display-9">{order.deliveredAt?.substring(0, 10)}</div> */}
                                    <Badge bg='info'>{order.deliveredAt?.substring(0, 10)}</Badge>
                                </Col>)}
                                
                                <Row className="mt-3"></Row>

                                {/* PAID BUTTON */}
                                {loadingMarkPaid && <Loader />}
                                {userInfo?.isAdmin && !order.isPaid ? (
                                    <Button
                                    className="w-100 py-2 action-button paid-btn"
                                    onClick={markPaidHandler}
                                    >
                                    <FaMoneyBillWave className="me-2" /> Markeer als Betaald
                                    </Button>
                                ) : ( 
                                    <Col>
                                        <div className="fw-semibold">
                                            { !order.isPaid ? <FaQuestion className="text-primary fs-6 mb-2" /> : <FaCheck className="text-primary fs-6 mb-2" /> }
                                            {' '}Betaald
                                        </div>
                                        {/* <div className="fw-semibold">{order.paidAt?.substring(0, 10)}</div> */}
                                        <Badge bg='success'>{order.paidAt?.substring(0, 10)}</Badge>
                                    </Col>
                                )}
                            </Col>
                        </Row>
                    </Card>
                </Col>
            </Row>

            {order.shippingLabel && (
                <Card className="mb-4 border-primary shadow-sm">
                    <Card.Body className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-1">
                                <h5 className="mb-0">DHL-verzendlabel</h5>
                                <Badge bg={hasAvailableShippingLabel ? 'success' : shippingLabel.status === 'error' ? 'danger' : 'secondary'}>
                                    {hasAvailableShippingLabel ? 'Beschikbaar' : shippingLabel.status === 'error' ? 'Niet beschikbaar' : 'In behandeling'}
                                </Badge>
                            </div>
                            {shippingLabel.trackingNumber && (
                                <div className="text-muted">
                                    Track-and-trace: <strong>{shippingLabel.trackingNumber}</strong>
                                    {' · '}
                                    <a href={`/order/${orderId}/track`}>
                                        Volg zending
                                    </a>
                                </div>
                            )}
                            {shippingLabel.errorMessage && (
                                <Alert variant="warning" className="mt-2 mb-0 py-2">
                                    {shippingLabel.errorMessage}
                                </Alert>
                            )}
                        </div>
                        {hasAvailableShippingLabel && (
                            <div className="d-flex flex-wrap gap-2">
                                <Button variant="outline-primary" onClick={() => navigate(`/order/${orderId}/label`)}>
                                    Bekijk label
                                </Button>
                                <Button variant="primary" onClick={downloadShippingLabel} disabled={downloadingLabel}>
                                    {downloadingLabel ? 'Downloaden…' : 'Download PDF'}
                                </Button>
                            </div>
                        )}
                    </Card.Body>
                </Card>
            )}

            {/* Search (attractive centered 50% desktop) */}
            <div className="my-3 mx-auto" style={{ maxWidth: '600px', width: '100%' }}>
                <Form.Control
                type="text"
                placeholder="Zoek op ISBN, titel of auteur..."
                value={search}
                onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                }}
                style={{
                    borderRadius: '12px',
                    padding: '12px 16px',
                    border: '2px solid #1e4778ff',
                    boxShadow: '0 0 20px rgba(16, 96, 216, 0.12)',
                    fontSize: '16px',
                }}
                />
            </div>

            {/* Items table */}
            <Tabs defaultActiveKey="items" className="mb-3">
                <Tab eventKey="items" title={`Boeken Lijst (${totalItems})`}>
                    <Card className="mb-3">
                        <Card.Body>
                        <h4>Boeken Lijst</h4>

                        {paginated.length === 0 ? (
                            <Message variant="info">Geen boeken gevonden.</Message>
                        ) : (
                            <Table striped bordered hover responsive>
                            <thead>
                                <tr>
                                    <th onClick={() => handleSort('isbn')} style={{ cursor: 'pointer' }}>
                                        ISBN {renderSortIcon('isbn')}
                                    </th>
                                    <th onClick={() => handleSort('title')} style={{ cursor: 'pointer' }}>
                                        Titel {renderSortIcon('title')}
                                    </th>
                                    <th onClick={() => handleSort('author')} style={{ cursor: 'pointer' }}>
                                        Auteur {renderSortIcon('author')}
                                    </th>
                                    <th onClick={() => handleSort('price')} style={{ cursor: 'pointer' }}>
                                        Prijs {renderSortIcon('price')}
                                    </th>
                                    {/* <th>Status</th> */}
                                    {!order?.isDelivered && <th>Accepteer</th>}
                                    {userInfo?.isAdmin && !order?.isDelivered && <th>Verwijder</th>}
                                </tr>
                            </thead>

                            <tbody>
                                {paginated.map((it, idx) => (
                                <tr key={it._id || it.isbn + '-' + idx} className={it._status === 'deleted' ? 'table-danger opacity-75' : ''}>
                                    <td>{it.isbn}</td>
                                    <td>{it.title}</td>
                                    <td>{it.author}</td>
                                    <td>
                                        € {Number(it.accepted_price ?? it.offer_price ?? it.price ?? 0).toFixed(2)}
                                        
                                        {it.accepted_price !== undefined && it.accepted_price !== null && (
                                            <div
                                            style={{
                                                fontSize: "11px",
                                                color: "#6c757d",
                                                fontStyle: "italic",
                                                lineHeight: "1.2",
                                            }}
                                            >
                                            partially accepted
                                            </div>
                                        )}
                                    </td>
                                    {/* <td>
                                    {it._status === 'deleted' ? (
                                        <Badge bg="danger">Deleted</Badge>
                                    ) : (
                                        <Badge bg="success">Active</Badge>
                                    )}
                                    </td> */}
                                    {!order?.isDelivered && (
                                    <td>
                                        <label className="switch">
                                        <input
                                            type="checkbox"
                                            checked={it.isAccepted || false}
                                            onChange={() => handleToggleAccept(it._id)}
                                            disabled={!userInfo?.isAdmin}   // DISABLE if NOT admin
                                            style={{
                                                opacity: !userInfo?.isAdmin ? 0.5 : 1,
                                                cursor: !userInfo?.isAdmin ? "not-allowed" : "pointer"
                                            }}
                                        />
                                        <span className="slider"></span>
                                        </label>

                                    </td>
                                    )}
                                    {userInfo?.isAdmin && !order?.isDelivered && it._status === 'active' && (
                                    <td>
                                        <Button
                                        variant="outline-danger"
                                        // className="border-0"
                                        onClick={() => handleDeleteItem(it._id)}
                                        disabled={!userInfo?.isAdmin}   // DISABLE if NOT admin
                                        style={{
                                            opacity: !userInfo?.isAdmin ? 0.5 : 1,
                                            cursor: !userInfo?.isAdmin ? "not-allowed" : "pointer"
                                        }}
                                        >
                                        <FaTrash style={{ fontSize: "18px" }} />
                                        </Button>
                                    </td>
                                    )}
                                    {userInfo?.isAdmin && !order?.isDelivered && it._status === 'deleted' && (
                                    <td className="text-muted fst-italic">
                                        Verwijderd
                                    </td>
                                    )}
                                </tr>
                                ))}
                            </tbody>
                            </Table>
                        )}

                        {/* Pagination */}
                        <div className="d-flex justify-content-between align-items-center">
                            <Button
                            disabled={page === 1}
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                            >
                            Vorige
                            </Button>

                            <span>
                            Pagina {page} van {totalPages} - {sorted.length} Boeken
                            </span>

                            <Button
                            disabled={page === totalPages}
                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                            >
                            Volgende
                            </Button>
                            {/* Right: page size selector */}
                            <div className="d-flex align-items-center gap-2">
                                <span className="text-muted">Per pagina:</span>
                                <Form.Select
                                size="sm"
                                value={pageSize}
                                onChange={(e) => {
                                    setPageSize(Number(e.target.value));
                                    setPage(1); // important
                                }}
                                style={{ width: '90px' }}
                                >
                                    <option value={10}>10</option>
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                </Form.Select>
                            </div>
                            <span className="text-muted">
                                Toon {(page - 1) * pageSize + 1}–
                                {Math.min(page * pageSize, sorted.length)} van {sorted.length}
                            </span>
                        </div>
                        {/* <div className="d-flex justify-content-center">
                            <Pagination className="mb-0">
                                {[...Array(totalPages).keys()].map((n) => (
                                <Pagination.Item key={n + 1} active={n + 1 === page} onClick={() => setPage(n + 1)}>
                                    {n + 1}
                                </Pagination.Item>
                                ))}
                            </Pagination>
                        </div> */}
                        </Card.Body>
                    </Card>
                </Tab>
                <Tab eventKey="removed" title={`Verwijderd Boeken (${totalRemoved})`}>
                    <Card className="mt-4">
                        <Card.Body>
                            <h4>Removed Items</h4>

                            {order.removedItems?.length === 0 ? (
                            <Message>No removed items yet</Message>
                            ) : (
                            <Table striped bordered hover>
                                <thead>
                                <tr>
                                    <th>ISBN</th>
                                    <th>Title</th>
                                    <th>Author</th>
                                    {
                                    userInfo?.isAdmin && !order?.isDelivered &&
                                        <th>Undo</th>
                                    }
                                </tr>
                                </thead>

                                <tbody>
                                    {order.removedItems.map((it) => (
                                    <tr key={it._id}>
                                        <td>{it.isbn}</td>
                                        <td>{it.title}</td>
                                        <td>{it.author}</td>
                                        {userInfo?.isAdmin && !order?.isDelivered &&
                                            <td>
                                                <Button
                                                className="undo-btn"
                                                onClick={() => handleUndoItem(it._id)}
                                                >
                                                <FaUndo /> Undo
                                                </Button>
                                            </td>
                                        }
                                    </tr>
                                    ))}
                                </tbody>
                            </Table>
                            )}
                        </Card.Body>
                    </Card>
                </Tab>
            </Tabs>
            <style>
            {`
            .order-summary-card {
                background: linear-gradient(to bottom right, #e0e9f5ff, #f7f9ff);
                border: 1px solid #e4e7f0;
            }

            .summary-block {
                transition: transform 0.15s ease, background-color 0.25s ease;
                padding: 10px;
                border-radius: 10px;
            }

            .summary-block:hover {
                background-color: #f5f7ff;
                transform: translateY(-3px);
            }

            .separator {
                width: 1px;
                height: 40px;
                background-color: #0b0a0aff;
                margin: auto;
            }

            .action-button {
                font-size: 1rem;
                font-weight: 600;
                min-height: 45px;
                border-radius: 10px !important;
                transition: background-color 0.2s ease, transform 0.15s ease;
            }

            .action-button:hover {
                transform: translateY(-2px);
            }

            .deliver-btn {
                background-color: #0d6efd;
                border-color: #1b6be2ff;
            }
            .deliver-btn:hover {
                background-color: #0b2f64ff;
            }

            .paid-btn {
                background-color: #28a745;
                border-color: #28a745;
            }
            .paid-btn:hover {
                background-color: #0c6e21ff;
            }
            .switch {
            position: relative;
            display: inline-block;
            width: 46px;
            height: 24px;
            }

            .switch input { display: none; }

            .slider {
            position: absolute;
            cursor: pointer;
            top: 0; left: 0;
            right: 0; bottom: 0;
            background-color: #ccc;
            transition: .4s;
            border-radius: 34px;
            }

            .slider:before {
            position: absolute;
            content: "";
            height: 18px;
            width: 18px;
            left: 3px;
            bottom: 3px;
            background-color: white;
            transition: .4s;
            border-radius: 50%;
            }

            input:checked + .slider {
            background-color: #28a745;
            }

            input:checked + .slider:before {
            transform: translateX(22px);
            }

            .undo-btn {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            background: #0d6efd10;
            color: #0d6efd;
            border: 1px solid #0d6efd;
            padding: 6px 12px;
            border-radius: 10px;
            transition: 0.25s ease;
            }

            .undo-btn:hover {
            background: #0d6efd;
            color: white;
            transform: scale(1.05);
            }
            `}
            </style>
        </>
    );
};

export default OrderScreen;